import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Select, Spin } from 'antd';
import axios from 'axios';
import debounce from 'lodash.debounce';

/**
 * EmrProviderAutocomplete - Autocomplete component for selecting EMR providers
 * (internal providers within the EMR system that documents can be assigned to).
 *
 * This is distinct from ReferringProviderAutocomplete which is for external
 * referring providers.
 */
const EmrProviderAutocomplete = ({ value, onChange, placeholder }) => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedValue, setSelectedValue] = useState(value || null);
  const [isWaitingForFetch, setIsWaitingForFetch] = useState(false);

  // Define the core async fetching logic using useCallback
  const coreFetchLogic = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setData([]);
      setFetching(false);
      setIsWaitingForFetch(false);
      return;
    }
    setFetching(true);
    try {
      const response = await axios.get('/api/v1/emr_providers', {
        params: {
          contains: searchQuery,
        },
      });
      const providers = response.data.providers || [];
      setData(providers);
    } catch (error) {
      console.error('Error fetching EMR providers:', error);
    } finally {
      setFetching(false);
      setIsWaitingForFetch(false);
    }
  }, [setData, setFetching, setIsWaitingForFetch]);

  // Create the debounced function using useMemo
  const fetchProviders = useMemo(
    () => debounce(coreFetchLogic, 500),
    [coreFetchLogic],
  );

  // Cleanup effect to cancel debounce on unmount
  useEffect(() => {
    return () => {
      fetchProviders.cancel();
    };
  }, [fetchProviders]);

  // Effect to handle initial value prop
  useEffect(() => {
    setSelectedValue(value || null);
    if (value && !searchText) {
      // UUID slugs won't match via the search endpoint — fetch directly by slug instead
      const isSlug = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
      if (isSlug) {
        const fetchBySlug = async () => {
          setFetching(true);
          try {
            const response = await axios.get(`/api/v1/emr_providers/${value}`);
            const provider = response.data;
            if (provider?.id) {
              setData([{ id: provider.id, name: provider.name }]);
            }
          } catch (error) {
            console.error('Error fetching provider by slug:', error);
          } finally {
            setFetching(false);
          }
        };
        fetchBySlug();
      } else {
        fetchProviders(value);
      }
    }
  }, [value]);

  const handleSearch = (newValue) => {
    setSearchText(newValue);
    if (newValue && newValue.length >= 2) {
      setIsWaitingForFetch(true);
    } else {
      setIsWaitingForFetch(false);
      setData([]);
      fetchProviders.cancel();
    }
    fetchProviders(newValue);
  };

  const handleChange = (newValue) => {
    setSelectedValue(newValue || null);
    setIsWaitingForFetch(false);
    setFetching(false);
    fetchProviders.cancel();

    // Pass the ID to the parent component
    onChange(newValue || '');
  };

  const handleClear = () => {
    setSelectedValue(null);
    setSearchText('');
    setData([]);
    setIsWaitingForFetch(false);
    setFetching(false);
    fetchProviders.cancel();
    onChange('');
  };

  const getNotFoundContent = () => {
    if (fetching || isWaitingForFetch) {
      return <Spin size="small" />;
    }

    if (!searchText || searchText.length < 2) {
      return 'Type to search providers';
    }

    return 'No providers found';
  };

  return (
    <Select
      showSearch
      value={selectedValue}
      placeholder={placeholder || 'Search providers...'}
      defaultActiveFirstOption={false}
      filterOption={false}
      onSearch={handleSearch}
      onChange={handleChange}
      onClear={handleClear}
      notFoundContent={getNotFoundContent()}
      allowClear
      getPopupContainer={(trigger) => trigger.parentNode}
    >
      {data.map((provider) => (
        <Select.Option key={provider.id} value={provider.id}>
          {provider.name || provider.id}
        </Select.Option>
      ))}
    </Select>
  );
};

EmrProviderAutocomplete.defaultProps = {
  value: '',
  placeholder: 'Search providers...',
};

EmrProviderAutocomplete.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default EmrProviderAutocomplete;
